import type { ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { PixlideLogo, LanguageSwitcher, cn } from '@pixlide/ui';
import { AuthShowcase } from '@/features/auth/AuthShowcase';

/**
 * Cadre commun aux écrans publics : formulaire à gauche, argumentaire produit à droite.
 *
 * L'écran est découpé en deux colonnes plein écran plutôt qu'en carte centrée : c'est la
 * structure de la maquette, et elle a un intérêt concret — la colonne de droite occupe une
 * place autrement perdue pour rassurer un visiteur qui découvre le produit, sans allonger
 * le formulaire.
 * Sous `lg`, la colonne vitrine disparaît et le formulaire prend toute la largeur : sur
 * mobile, la seule chose attendue de cet écran est de pouvoir se connecter.
 */
export function AuthLayout({
  tab,
  title,
  subtitle,
  children,
  footer,
}: {
  /** Onglet actif. Omis sur les écrans hors parcours (confirmation d'email). */
  tab?: 'login' | 'register';
  title: string;
  subtitle: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="flex min-h-dvh">
      <div className="auth-canvas flex min-w-0 flex-1 flex-col px-6 py-7 sm:px-10 lg:px-14">
        <header className="flex items-center justify-between gap-4">
          <PixlideLogo />
          <LanguageSwitcher />
        </header>

        <main className="mx-auto flex w-full max-w-[24rem] flex-1 flex-col justify-center py-12">
          {tab === undefined ? null : <AuthTabs active={tab} />}

          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          <p className="mt-1 text-[13px] leading-relaxed text-ink-500">{subtitle}</p>

          <div className="mt-6">{children}</div>

          {footer ? <div className="mt-5 text-center text-[13px] text-ink-500">{footer}</div> : null}
        </main>

        <footer className="text-center text-[11px] text-ink-300">
          © {new Date().getFullYear()} Pixlide
        </footer>
      </div>

      <AuthShowcase />
    </div>
  );
}

/** Bascule connexion / inscription, en tête de formulaire. */
function AuthTabs({ active }: { active: 'login' | 'register' }) {
  return (
    // Une <nav> et des liens, pas un composant à onglets : les deux écrans ont chacun leur
    // URL, donc leur signet, leur bouton « précédent » et leur partage. Un onglet en
    // JavaScript casserait ces trois comportements.
    <nav aria-label="Connexion ou inscription" className="mb-9 flex gap-7 border-b border-ink-100">
      <TabLink to="/connexion" isActive={active === 'login'}>
        Se connecter
      </TabLink>
      <TabLink to="/inscription" isActive={active === 'register'}>
        S’inscrire
      </TabLink>
    </nav>
  );
}

function TabLink({
  to,
  isActive,
  children,
}: {
  to: '/connexion' | '/inscription';
  isActive: boolean;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      search={{}}
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        // `-mb-px` fait chevaucher le trait de l'onglet actif et celui de la barre : sans
        // cela, les deux se superposent et donnent une ligne de 2px par endroits.
        '-mb-px border-b-2 pb-3 text-[13px] transition',
        isActive
          ? 'border-ink-900 font-medium text-ink-900'
          : 'border-transparent text-ink-300 hover:text-ink-700',
      )}
    >
      {children}
    </Link>
  );
}

/**
 * Bandeau d'erreur de formulaire, pour les erreurs qui ne portent pas sur un champ précis
 * (identifiants invalides, compte verrouillé, compte non confirmé).
 */
export function FormAlert({ message, action }: { message: string; action?: ReactNode }) {
  return (
    <div
      // `role="alert"` : le message est annoncé dès son apparition, sans quoi un
      // utilisateur de lecteur d'écran ne saurait pas pourquoi rien ne se passe.
      role="alert"
      className="mb-4 rounded-xl bg-danger-100 px-4 py-3 text-[13px] font-medium text-danger-500"
    >
      {message}

      {/* L'action vit DANS l'alerte : une erreur qui indique la sortie évite d'avoir à la
          chercher ailleurs sur l'écran, et reste annoncée avec le message. */}
      {action ? <div className="mt-2">{action}</div> : null}
    </div>
  );
}
